import { useEffect, useRef, useState } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { runInAction } from 'mobx';
import PropTypes from 'prop-types';
import Sms from './components/Confirmations/Sms';
import SwitchConfirmationMethodScreen, {
  getUnavailableConfirmationTypes
} from './components/SwitchConfirmationMethodScreen';
import { ConfirmationModal } from 'components/common/ConfirmationModal/ConfirmationModal';
import { PASSWORD_RECOVERY_STATUSES, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
import {
  clearLocalStorage,
  clearStoredTokens,
  getAvailableConfirmationTypes,
  getMaskedEmail,
  getMaskedPhone
} from 'services/authUtils';
import { loginPin } from 'services/requestAgent';
import i18n from '../../../i18n';

// Only ever rendered from the login (components/Authorization) and password-recovery
// (components/PasswordRecovery) pages, i.e. always in the anonymous flow. TRN-6358: this used to
// gate on `!userStore.isInitialized`, but a second tab opened on /login while another tab is
// already logged in shares that tab's localStorage token, so App.js initializes userStore even on
// /login — the gate then returned null, the 2FA modal never appeared and the Log In button stayed
// in its loading state forever. Whether the modal shows is decided by the caller (it mounts this
// component only once the credentials step succeeded), not by the user-data state.
const ActionConfirmation = ({ authStore, passwordRecoveryStore, isPasswordRecovery = false }) => {
  const pushPollingIntervalRef = useRef(null);
  const store = isPasswordRecovery ? passwordRecoveryStore : authStore;
  // Guards Escape/backdrop-click while a method switch is in flight — see SwitchConfirmationMethodScreen.
  const [isSwitchingMethod, setIsSwitchingMethod] = useState(false);

  useEffect(() => {
    if (!store.isShowSwitchConfirmationMethodScreen) {
      setIsSwitchingMethod(false);
    }
  }, [store.isShowSwitchConfirmationMethodScreen]);

  useEffect(() => {
    // Start polling for push notification confirmation
    if (store.confirmationActionType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && store.confirmationId) {
      pushPollingIntervalRef.current = setInterval(async () => {
        try {
          // Call confirmation endpoint with null code for push notification
          // Backend will respond with CONFIRMATION_PENDING_ACTION until mobile confirms
          const response = await loginPin(null, store.confirmationId);

          if (response.success) {
            clearInterval(pushPollingIntervalRef.current);
            store.setIsUserAlreadyRegistered(false);
            store.setIsPinSuccess(true);
          }
        } catch (error) {
          // Check for specific error codes
          if (error?.code === 'CONFIRMATION_REJECTED') {
            clearInterval(pushPollingIntervalRef.current);
            // Reset to login form using runInAction for proper MobX updates
            runInAction(() => {
              store.confirmationActionType = null;
              store.confirmationId = null;
              if (isPasswordRecovery) {
                // Reset password recovery flow
                store.recoveryStatus = null;
                store.error = {
                  code: 'CONFIRMATION_REJECTED',
                  message: i18n.getMessage
                    ? i18n.getMessage('error.confirmationRejected')
                    : 'Push notification was rejected. Please try again.'
                };
              } else {
                // Reset login flow
                store.isLoginSuccess = false;
                store.isPushNotificationEnabled = false;
                store.error = {
                  code: 'CONFIRMATION_REJECTED',
                  message: i18n.getMessage
                    ? i18n.getMessage('error.confirmationRejected')
                    : 'Push notification was rejected. Please try again.'
                };
              }
            });
          } else if (error?.code === 'CONFIRMATION_PENDING_ACTION') {
            // Continue polling, mobile hasn't confirmed yet
          } else {
            // Handle other errors
            // eslint-disable-next-line no-console
            console.error('Error during push confirmation:', error);
          }
        }
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (pushPollingIntervalRef.current) {
        clearInterval(pushPollingIntervalRef.current);
      }
    };
  }, [store, store.confirmationActionType, store.confirmationId]);

  const handleCode = (code, disableSend) => {
    store.setCode(code);
    if (!disableSend && !store.isLoading) {
      if (isPasswordRecovery) {
        if (store.recoveryStatus === PASSWORD_RECOVERY_STATUSES.PHONE_CONFIRM) {
          store.sendPhoneCode();
        }
      } else {
        store.sendCode();
      }
    }
  };
  const resendSecurityCode = () => {
    if (isPasswordRecovery) {
      if (store.recoveryStatus === PASSWORD_RECOVERY_STATUSES.PHONE_CONFIRM) {
        store.resendPhoneCode();
      }
    } else {
      store.resendSecurityCode();
    }
  };

  // Cancel-and-return-to-the-previous-step: authStore.reset() flips isLoginSuccess back to
  // false (falls back to the credentials form); passwordRecoveryStore.resetToPhoneEntry()
  // clears recoveryStatus (falls back to the Phone screen, which stays mounted behind this
  // modal — see PasswordRecoveryStages).
  const onClose = isPasswordRecovery
    ? () => store.resetToPhoneEntry()
    : () => {
        // The credentials step already stored a token that isn't fully authorized until the code is entered —
        // drop it, otherwise navigating on from the login page (e.g. "Sign up") is treated by App.js as being
        // logged in and ends in a 401 → /logout → back to /login.
        clearStoredTokens();
        store.reset();
      };

  if (store.isShowSwitchConfirmationMethodScreen) {
    return (
      // Escape/backdrop-click go "back" to the code-entry screen (same as the screen's own
      // "Close" footer button) rather than all the way out to the credentials form — but not
      // while a switch is in flight, so it can't be dismissed mid-request.
      <ConfirmationModal
        show
        shadow={'glow'}
        onClose={() => !isSwitchingMethod && store.setIsShowSwitchConfirmationMethodScreen(false)}
      >
        <SwitchConfirmationMethodScreen
          onSwitchMethod={store.forceSwitchToAnotherConfirmationType}
          onBack={() => store.setIsShowSwitchConfirmationMethodScreen(false)}
          onSwitchingChange={setIsSwitchingMethod}
          currentConfirmationType={store.confirmationActionType}
          availableConfirmationTypes={getAvailableConfirmationTypes()}
          unavailableConfirmationTypes={getUnavailableConfirmationTypes(store.error)}
        />
      </ConfirmationModal>
    );
  } else {
    // TRN-7254: every confirmation type (SMS/WhatsApp/Email/Google Authenticator/push
    // notification) goes through the same modal now — Google Auth and push used to render
    // their own old, unstyled Google2FA/PushNotification2FA components (deleted).
    const isEmailConfirmation = store.confirmationActionType === TWO_FACTOR_AUTH_TYPES.EMAIL;
    const phone = isPasswordRecovery ? store.generalFields.phoneNumber : store.phone;
    // The email confirmation code is sent to the login email (principal); mask it when known —
    // e.g. logging in via the Phone tab means no email was ever collected, so this (deliberately)
    // comes back empty and Sms.js falls back to a destination-less sentence instead of leaving
    // "sent to " with nothing after it (see getConfirmationSubtitle).
    const maskedEmail =
      !isPasswordRecovery && store.values.username?.includes('@') ? getMaskedEmail(store.values.username) : '';

    return (
      // onClose is also passed to ConfirmationModal itself (not just Sms's header ✕ below) so
      // Escape/backdrop-click use the same cancel handler instead of silently doing nothing.
      <ConfirmationModal show shadow={'neutral'} onClose={onClose}>
        <Sms
          type={store.confirmationActionType}
          phone={isEmailConfirmation ? maskedEmail : getMaskedPhone(phone)}
          sms={store.values.code}
          error={store.error}
          handleSms={handleCode}
          resendCode={resendSecurityCode}
          isForgotPassword={isPasswordRecovery}
          onClose={onClose}
        />
      </ConfirmationModal>
    );
  }
};

ActionConfirmation.propTypes = {
  authStore: MobXPropTypes.observableObject,
  passwordRecoveryStore: MobXPropTypes.observableObject,
  isPasswordRecovery: PropTypes.bool
};

export default inject('authStore', 'passwordRecoveryStore')(observer(ActionConfirmation));
